import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { rankCountries } from '../constants/exploreFilterEngine';

/**
 * Combined Location filter — two sub-controls in one popover:
 *   - Country: checkbox list (typically DE / AT / CH), derived by splitting
 *     `headquarters` on its last comma.
 *   - City: substring text input matched against the city portion.
 * Both can be active together; the engine ANDs them inside this single column.
 *
 * Filter shape passed up: `{ values?: Set<countryName>, text?: string }`.
 * Empty filter → onChange(null) so the column-filter entry is removed.
 */
const FilterLocation = observer(({ value, onChange, results }) => {
  const countries = rankCountries(results);
  const selectedCountries = value?.values || new Set();
  const cityText = value?.text || '';

  const updateCountries = (countryName) => {
    const next = new Set(selectedCountries);
    if (next.has(countryName)) next.delete(countryName);
    else next.add(countryName);
    const cleaned = {
      ...(next.size > 0 ? { values: next } : {}),
      ...(cityText ? { text: cityText } : {})
    };
    onChange(Object.keys(cleaned).length === 0 ? null : cleaned);
  };

  const updateCity = (text) => {
    const cleaned = {
      ...(selectedCountries.size > 0 ? { values: selectedCountries } : {}),
      ...(text ? { text } : {})
    };
    onChange(Object.keys(cleaned).length === 0 ? null : cleaned);
  };

  return (
    <div className="filter-location">
      <div className="filter-section">
        <div className="filter-section-title">Country</div>
        <div className="filter-checkbox-list">
          {countries.length === 0 ? (
            <div className="filter-empty">No country data in current results.</div>
          ) : (
            countries.map(([name, count]) => (
              <label key={name} className="filter-checkbox-row">
                <input
                  type="checkbox"
                  checked={selectedCountries.has(name)}
                  onChange={() => updateCountries(name)}
                />
                <span className="filter-checkbox-label">{name}</span>
                <span className="filter-checkbox-count">{count.toLocaleString('en-US')}</span>
              </label>
            ))
          )}
        </div>
      </div>
      <div className="filter-section">
        <div className="filter-section-title">City (substring)</div>
        <input
          type="text"
          className="filter-text-input"
          placeholder="e.g. München, Tirol..."
          aria-label="City substring"
          value={cityText}
          onChange={(e) => updateCity(e.target.value)}
        />
      </div>
    </div>
  );
});

FilterLocation.propTypes = {
  value: PropTypes.shape({
    values: PropTypes.object, // Set<string>
    text: PropTypes.string
  }),
  onChange: PropTypes.func.isRequired,
  results: PropTypes.array.isRequired
};

export default FilterLocation;
